import RootState from '../RootState';
import {
  createNewFlyer,
  createNewTemplate,
  getFlyer,
  saveFlyer,
  updateTemplate
} from '../../utils/FirebaseUtils';
import SaveState from '../SaveState';
import { flyerStateToData, getAllStorableAssets } from '../FlyerState';
import {
  uploadAsset,
  deleteFileWithUrl,
  deleteFile
} from '../../utils/S3Utils';
import * as Actions from './';
import { getStoreState, getRenderer, getStage } from '../Store';
import { compressAudioFile } from '../../utils/index';
import {
  getStageScreenshotFile,
  isExtensionImage
} from '../../utils/GraphicsUtils';
import { uploadFile, getBaseURL } from '../../utils/S3Utils';
import { reportBFError } from '../../utils/Tracker';
import { instanceOfResolution } from '../../data/Resolution';
import FlyerType from '../../data/FlyerType';
import { promptAnswer } from 'components/PromptView';

const getNextFileToUpload = (): File | null => {
  const flyerState = getStoreState().flyer;
  const uploadableAssets = getAllStorableAssets(flyerState).filter(item => {
    // console.log('TEST URL: ' + isUrlBlob(item.url));
    return item.file !== null;
  });
  if (uploadableAssets.length) {
    return uploadableAssets[0]!.file;
  }
  return null;
};

const getIdsLinkedToFile = (file: File): string[] => {
  const result: string[] = [];
  const flyerState = getStoreState().flyer;
  const uploadableAssets = getAllStorableAssets(flyerState);
  uploadableAssets.forEach(asset => {
    if (asset.file === file) {
      result.push(asset.id);
      asset.file = null;
    }
  });
  return result;
};

const updateAssetsUrl = (ids: string[], url: string): boolean => {
  const flyerState = getStoreState().flyer;
  const uploadableAssets = getAllStorableAssets(flyerState);
  let hasUpdated = false;
  uploadableAssets.forEach(asset => {
    if (ids.indexOf(asset.id) > -1) {
      asset.url = url;
      hasUpdated = true;
    }
  });
  return hasUpdated;
};

const uploadNextAssetIfNeeded = async (): Promise<string | null> => {
  var file = getNextFileToUpload();
  if (!file) {
    return null;
  }
  const ids = getIdsLinkedToFile(file);
  const extension = (file.name.split('.').pop() as string).toLowerCase();
  if (extension === 'wav') {
    file = await compressAudioFile(file);
  }
  const url = await uploadAsset(file);
  const hasUpdated = updateAssetsUrl(ids, url);
  if (!hasUpdated) {
    await deleteFile(url.split('/').pop() as string);
  }
  return url;
};

const uploadAssetsIfNeeded = async (): Promise<string[]> => {
  const uploadedAssets: string[] = [];
  let newUrl = await uploadNextAssetIfNeeded();
  while (newUrl) {
    const extension = newUrl.split('.').pop()!;
    if (isExtensionImage(extension)) {
      uploadedAssets.push(newUrl);
    }
    newUrl = await uploadNextAssetIfNeeded();
  }
  return uploadedAssets;
};

const deleteUnusedAssetsIfNeeded = async (removedAssets: string[]) => {
  const urls = [...removedAssets];
  for (const assetUrl of urls) {
    try {
      await deleteFileWithUrl(assetUrl);
      const index = removedAssets.indexOf(assetUrl);
      removedAssets.splice(index, 1);
    } catch {
      // do nothing
    }
  }
};

const onProjectSaved = () => {
  return (dispatch: Function, getState: () => RootState) => {
    const flyerState = getState().flyer;
    const state = flyerState.hasNewChanges
      ? SaveState.Unsaved
      : SaveState.Saved;
    dispatch({ type: Actions.SAVE_STATE_CHANGED, state, time: Date.now() });
  };
};

const testSavedFlyer = async (flyerId: string, data: any) => {
  const flyer = await getFlyer(flyerId, false);
  if (flyer) {
    if (!flyer.resolution) {
      const msg =
        `Flyer ${flyerId} hasn't been saved properly: ` + JSON.stringify(data);
      console.error(msg);
      reportBFError(new Error(msg));
    } else {
      console.log('Flyer has been saved correctly');
    }
  }
};

export const saveProject = () => {
  return async (dispatch: Function, getState: () => RootState) => {
    // update screenshot only if layers changed
    const flyerState = getState().flyer;
    const { resolution, type } = flyerState;
    if (!resolution || !instanceOfResolution(resolution)) {
      alert(
        'Could not save the flyer because the resolution is not defined. Please try again and if the problem persists contact customer support.'
      );
      // https://stackoverflow.com/questions/11616630/how-can-i-print-a-circular-structure-in-a-json-like-format
      var cache: any = [];
      const msg = JSON.stringify(getState().flyer, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          // Duplicate reference found, discard key
          if (cache.includes(value)) {
            return;
          }
          // Store value in our collection
          cache.push(value);
        }
        return value;
      });
      cache = null; // Enable garbage collection
      reportBFError(
        new Error('Resolution is corrupted. Flyer state is ' + msg)
      );
      return;
    }
    dispatch({ type: Actions.SAVE_STATE_CHANGED, state: SaveState.Saving });
    try {
      const isEditingTemplate = type === FlyerType.Template;
      if (isEditingTemplate) {
        const templateData = flyerStateToData(getState().flyer, true);
        dispatch({ type: Actions.WILL_SAVE_FLYER });
        await updateTemplate(flyerState.id!, templateData);
        dispatch(onProjectSaved());
        return;
      }

      const renderer = getRenderer();
      const stage = getStage();
      const thumbnail = await getStageScreenshotFile(
        resolution,
        renderer,
        stage,
        {
          width: 256,
          height: 256
        }
      );
      let flyerId = flyerState.id;
      let isNewFlayer = false;
      if (!flyerState.id) {
        isNewFlayer = true;
        let name = await promptAnswer('New flyer name?');
        if (!name) {
          dispatch({
            type: Actions.SAVE_STATE_CHANGED,
            state: SaveState.Unsaved
          });
          return;
        }
        name = name.trim();
        const uid = getState().app.user!.uid;
        flyerId = await createNewFlyer(name, uid);
        console.log('created flyer: ' + flyerId);
        dispatch({ type: Actions.FLYER_CREATED, id: flyerId, name });
      }
      const thumbnailName = flyerId + '.jpg';
      await uploadFile(thumbnailName, thumbnail, 'image/jpg');
      const uploadedImages = await uploadAssetsIfNeeded();
      const data = flyerStateToData(getState().flyer, true);
      const { templateUI, templateOptions } = getState().app;
      if (templateUI) {
        data.templateUI = templateUI;
      }
      if (templateOptions) {
        data.templateOptions = templateOptions;
      }
      data.thumbnailUrl = getBaseURL() + thumbnailName;
      if (uploadedImages.length) {
        data.uploadedImages = uploadedImages;
      }
      dispatch({ type: Actions.WILL_SAVE_FLYER });
      await saveFlyer(flyerId!, getState().app.user!.uid, data);
      await deleteUnusedAssetsIfNeeded(getState().flyer.removedAssets);
      dispatch(onProjectSaved());
      if (isNewFlayer) {
        const { user } = getState().app;
        window.history.replaceState('', '', `/flyers/${user?.uid}/${flyerId}`);
      }
      // Test saved project
      testSavedFlyer(flyerId!, data);
    } catch (error) {
      dispatch({
        type: Actions.SAVE_STATE_CHANGED,
        state: SaveState.Unsaved
      });
      alert(`Could not save the flyer because of ${error.message}`);
      reportBFError(
        new Error('Could not save the flyer because of ' + error.message)
      );
    }
  };
};

export const saveTemplate = () => {
  return async (dispatch: Function, getState: () => RootState) => {
    dispatch({ type: Actions.SAVE_STATE_CHANGED, state: SaveState.Saving });
    try {
      let name = await promptAnswer('New template name?');
      if (!name) {
        dispatch({
          type: Actions.SAVE_STATE_CHANGED,
          state: SaveState.Unsaved
        });
        return;
      }
      name = name.trim();
      const flyerId = await createNewTemplate(name);
      dispatch({ type: Actions.FLYER_CREATED, id: flyerId, name });
      // const uploadedImages = await uploadAssetsIfNeeded();
      const data = flyerStateToData(getState().flyer, true);
      data.published = false;
      data.date = Date.now();
      // if (uploadedImages.length) {
      //   data.uploadedImages = uploadedImages;
      // }
      dispatch({ type: Actions.WILL_SAVE_FLYER });
      await updateTemplate(flyerId, data);
      dispatch(onProjectSaved());
    } catch (error) {
      dispatch({
        type: Actions.SAVE_STATE_CHANGED,
        state: SaveState.Unsaved
      });
      alert(`Could not save the flyer because of ${error.message}`);
    }
  };
};

// export const assetUploaded = (id: string, url: string) => {
//   return async (dispatch: Function, getState: () => RootState) => {
//     const extension = url.split('.').pop() as string;
//     let hasUpdatedAsset = false;
//     if (isExtensionImage(extension)) {
//       // check frames
//       getState().flyer.layers.forEach(layer => {
//         layer.frames.forEach(frame => {
//           if (frame.id === id) {
//             frame.url = url;
//             hasUpdatedAsset = true;
//           }
//         });
//       });
//     } else {
//       // check soundtrack
//     }
//     if (hasUpdatedAsset) {
//       dispatch({
//         type: Actions.ASSET_UPLOADED
//       });
//     } else {
//       console.log('delete image because is not used anymore');
//       await deleteFile(url.split('/').pop() as string);
//     }
//   };
// };



// WEBPACK FOOTER //
// ./src/state/actions/cloud.ts